// vocabData.ts — eager index + lazy per-deck chunk loader for the Vocabulary
// Cheatsheet static asset (`frontend/data/sat/cheatsheet/vocab/`). Mirrors the
// relationLessons eager-import pattern; per-deck card/link content is loaded
// lazily via `import.meta.glob` (Vite chunk map — loader functions, NOT
// eager), version-keyed so a bundle swap (`content_version`) never resurrects
// a stale in-memory chunk cache. See
// docs/plans/2026-07-31-vocab-impl-reconciliation.md R1/R2.
import indexRaw from '@data/sat/cheatsheet/vocab/index.json';

export interface VocabTier {
  tier: number;
  label: string;
  rankStart: number;
  rankEnd: number;
}

export interface VocabIndexWord {
  rank: number;
  lemma: string;
  display_form?: string;
  pos: string;
  difficulty: string;
  tier: number;
  deck: number;
  asked_count: number;
  /** Deduplicated linked-question count emitted at asset build — the ONE
   * number both the browse row and the detail view render (design R2 F1). */
  question_count?: number;
  context_count: number;
}

export interface VocabIndex {
  content_version: string;
  n_words: number;
  deck_size: number;
  tiers: VocabTier[];
  words: VocabIndexWord[];
  /** Optional: count of distinct source exams behind the corpus, emitted by
   * the data pipeline. Absent (undefined) on older index builds, or `null`
   * when the pipeline hasn't computed it yet for this build — callers must
   * degrade gracefully (omit the stat) in both cases rather than assume it's
   * a number. */
  n_source_exams?: number | null;
  /** Optional, pipeline-emitted count of words with asked_count > 0.
   * Present in the shipped index, but we deliberately do NOT read it (see
   * `ASKED_WORD_COUNT` below) — deriving it client-side from `words` means
   * the home-card stat can never drift from what the ranked list itself
   * shows, independent of whether the pipeline's own count stays in sync. */
  n_asked_words?: number | null;
  /** Optional, pipeline-emitted distinct-question coverage (founder
   * directive 2026-07-31: "vocab in context and flashcard should also have
   * the question coverage stats as shown in the clue card"). Computed at
   * asset-build time from every word's links
   * (scripts/vocab/build_frontend_asset.py `compute_vocab_question_coverage`)
   * — never recomputed client-side, since the eager index doesn't carry the
   * full per-word links (those are lazy per-deck chunks). Null when the
   * build couldn't resolve a real denominator (see `n_rw_questions_from_
   * corpus_stats` docstring) — callers must omit the coverage block rather
   * than show a fabricated stat. See `./vocabCoverage.ts`. */
  coverage?: {
    /** Cumulative question coverage cut on the product importance tiers
     * (founder framing: "tier 1, 2, 3 covers x% test questions"). Computed
     * from the UNCAPPED per-question extraction units, not the capped
     * per-word links. Runtime-validated in ./vocabCoverage.ts — treat this
     * type as loose transport. */
    tiers: Array<{
      n: number;
      covered: number;
      pct: number;
      tier?: number;
      label?: string;
      coveredIndividual?: number;
      pctIndividual?: number;
    }>;
    denominator: {
      section: string;
      totalQuestions: number;
      /** Provenance label — this is a DIFFERENT, much larger universe than
       * relationCoverage.json's curated 5-exam audit denominator (270), so
       * it must self-describe rather than be silently compared to it. */
      label: string;
      corpusSnapshot: string;
    };
  } | null;
}

export interface VocabDeckCardEntry {
  rank: number;
  lemma: string;
  general_meaning: string | null;
  sat_sense: string | null;
  memory_hook: string | null;
  meaning_source: string | null;
  /** v1-simple drill-back fields (scripts/vocab/enrich_drill_fields.py), from
   * OUR OWN v1 vocab DB — up to 3 cleaned synonym strings. Absent/empty when
   * the pipeline found none for this word; callers must omit the Synonyms
   * section cleanly rather than render an empty chip row. */
  synonyms?: string[];
  /** A <=8-word terse meaning, verbatim from already-reviewed v1 content
   * (never LLM-generated by the pipeline). Null/absent when the v1 meaning
   * was too long to use verbatim — callers fall back to `sat_sense`. As of
   * the synonyms_final.json/cond_final.json rewire, this is an alias of
   * `sat_short` kept for backward compat with existing consumers. */
  short_gloss?: string | null;
  /** Final, human-reviewed short SAT-sense gloss, from
   * cond_final.json["meanings"][lemma].sat_short (scripts/vocab/
   * enrich_drill_fields.py). The authoritative short meaning going forward. */
  sat_short?: string | null;
  /** Final, human-reviewed short common/non-SAT-sense gloss, from
   * cond_final.json["meanings"][lemma].common_short. Legitimately null when
   * the word has no notable common sense worth showing. */
  common_short?: string | null;
}

export interface VocabDeckCardsChunk {
  content_version: string;
  deck: number;
  words: VocabDeckCardEntry[];
}

export interface VocabLinkEntry {
  id: string;
  role: 'asked' | 'choice' | 'passage' | string;
  llm_role?: string;
}

export interface VocabDeckLinksChunk {
  content_version: string;
  deck: number;
  links: Record<string, VocabLinkEntry[]>;
}

export const VOCAB_INDEX: VocabIndex = indexRaw as VocabIndex;
export const VOCAB_CONTENT_VERSION: string = VOCAB_INDEX.content_version;
export const VOCAB_DECK_SIZE: number = VOCAB_INDEX.deck_size;
export const TIERS: VocabTier[] = VOCAB_INDEX.tiers;

/** A word's stable id is its lemma — the emitter guarantees uniqueness + a
 * slug-safe alphabet, and lemma is the canonical join key across index/cards/
 * links (`display_form` is a surface label only). */
export function wordId(word: { lemma: string }): string {
  return word.lemma;
}

export const WORD_BY_ID: Record<string, VocabIndexWord> = Object.fromEntries(
  VOCAB_INDEX.words.map((w) => [wordId(w), w]),
);

/** Count of words with at least one real (asked) question appearance —
 * derived from the shipped index rather than hardcoded, so it never drifts
 * from the actual data. */
export const ASKED_WORD_COUNT: number = VOCAB_INDEX.words.filter((w) => w.asked_count > 0).length;

/** Words in rank order (the index is already emitted in rank order; this
 * export documents that invariant for callers that iterate for "prev/next"). */
export const RANK_ORDER: VocabIndexWord[] = VOCAB_INDEX.words;

export interface VocabDeck {
  deckId: number;
  tier: number;
  words: VocabIndexWord[];
}

export const DECKS: VocabDeck[] = (() => {
  const byDeck = new Map<number, VocabIndexWord[]>();
  for (const w of VOCAB_INDEX.words) {
    if (!byDeck.has(w.deck)) byDeck.set(w.deck, []);
    byDeck.get(w.deck)!.push(w);
  }
  return [...byDeck.keys()].sort((a, b) => a - b).map((deckId) => ({
    deckId,
    tier: byDeck.get(deckId)![0].tier,
    words: byDeck.get(deckId)!,
  }));
})();

export function deckIdOf(rank: number): number {
  // deck_size words per deck, rank is 1-based.
  return Math.ceil(rank / VOCAB_DECK_SIZE);
}

function deckFileId(deckId: number): string {
  return String(deckId).padStart(2, '0');
}

// Vite lazy chunk maps — NOT eager. Each entry is a loader function returning
// a promise of the module.
const cardChunkLoaders = import.meta.glob('../../../../../../../../data/sat/cheatsheet/vocab/cards/*.json');
const linkChunkLoaders = import.meta.glob('../../../../../../../../data/sat/cheatsheet/vocab/links/*.json');

// Version-keyed caches: `${contentVersion}:${deckId}` -> Promise<chunk>. A new
// bundle (different content_version) never reads a stale cached promise from
// a previous asset build.
const cardCache = new Map<string, Promise<VocabDeckCardsChunk>>();
const linkCache = new Map<string, Promise<VocabDeckLinksChunk>>();

function cacheKey(deckId: number): string {
  return `${VOCAB_CONTENT_VERSION}:${deckId}`;
}

export function loadDeckCards(deckId: number): Promise<VocabDeckCardsChunk> {
  const key = cacheKey(deckId);
  let cached = cardCache.get(key);
  if (!cached) {
    const path = `../../../../../../../../data/sat/cheatsheet/vocab/cards/deck_${deckFileId(deckId)}.json`;
    const loader = cardChunkLoaders[path];
    if (!loader) {
      cached = Promise.reject(new Error(`No card chunk for deck ${deckId}`));
    } else {
      // GAP "content-lookup failure" (docs/plans/2026-08-01-vocab-flashcard-
      // v1v2-audit.md): a rejected promise must NOT be cached forever — a
      // transient chunk-load failure (bad network, dev-server hiccup) would
      // otherwise permanently poison this deck for the rest of the session,
      // making a caller's "Retry" a no-op. Evict on failure so the next call
      // (e.g. a user pressing Retry) actually re-fetches.
      cached = loader()
        .then((mod: any) => (mod.default ?? mod) as VocabDeckCardsChunk)
        .catch((err) => {
          if (cardCache.get(key) === cached) cardCache.delete(key);
          throw err;
        });
    }
    cardCache.set(key, cached);
  }
  return cached;
}

export function loadDeckLinks(deckId: number): Promise<VocabDeckLinksChunk> {
  const key = cacheKey(deckId);
  let cached = linkCache.get(key);
  if (!cached) {
    const path = `../../../../../../../../data/sat/cheatsheet/vocab/links/deck_${deckFileId(deckId)}.json`;
    const loader = linkChunkLoaders[path];
    if (!loader) {
      cached = Promise.reject(new Error(`No link chunk for deck ${deckId}`));
    } else {
      cached = loader()
        .then((mod: any) => (mod.default ?? mod) as VocabDeckLinksChunk)
        .catch((err) => {
          if (linkCache.get(key) === cached) linkCache.delete(key);
          throw err;
        });
    }
    linkCache.set(key, cached);
  }
  return cached;
}

/** Fetches (and caches) the deck chunk for every distinct deck represented in
 * `words`, then returns a lemma-keyed map of card entries. Used by the
 * Starred browse view, whose page can mix words from up to 20 different
 * source decks (PM §3 "may mix source decks"). */
export async function loadCardsForWords(words: VocabIndexWord[]): Promise<Record<string, VocabDeckCardEntry>> {
  const deckIds = [...new Set(words.map((w) => w.deck))];
  const chunks = await Promise.all(deckIds.map((id) => loadDeckCards(id).catch(() => null)));
  const byLemma: Record<string, VocabDeckCardEntry> = {};
  for (const chunk of chunks) {
    if (!chunk) continue;
    for (const w of chunk.words) byLemma[w.lemma] = w;
  }
  return byLemma;
}

/** Convenience: card content for a single word (fetches — and caches — its
 * whole deck chunk, then picks the word out). */
export async function loadWordCard(id: string): Promise<VocabDeckCardEntry | null> {
  const word = WORD_BY_ID[id];
  if (!word) return null;
  const chunk = await loadDeckCards(word.deck);
  return chunk.words.find((w) => w.lemma === id) ?? null;
}

/** Convenience: link entries for a single word (fetches — and caches — its
 * whole deck chunk, then picks the word out). */
export async function loadWordLinks(id: string): Promise<VocabLinkEntry[]> {
  const word = WORD_BY_ID[id];
  if (!word) return [];
  const chunk = await loadDeckLinks(word.deck);
  return chunk.links[id] ?? [];
}

export function tierOf(tier: number): VocabTier | undefined {
  return TIERS.find((t) => t.tier === tier);
}

/** Word before / after a given rank in global rank order (undefined at the
 * ends). Used by the detail header prev/next strip. */
export function neighborsOf(rank: number): { prev?: VocabIndexWord; next?: VocabIndexWord } {
  const idx = rank - 1; // index in RANK_ORDER assumes contiguous rank 1..n; guard below
  const byRank = RANK_ORDER[idx]?.rank === rank ? idx : RANK_ORDER.findIndex((w) => w.rank === rank);
  if (byRank === -1) return {};
  return { prev: RANK_ORDER[byRank - 1], next: RANK_ORDER[byRank + 1] };
}
